import type { ChangeType } from "@rogoag/shared-ts";

// The exit interview's per-sample change log (Airtable "Sample Changes"). exit-interview-lambda
// writes one line per changed sample as `- Sample ${rogo_id.split(".")[0]},${change_type},
// ${change_reason}`, newline-joined. `change_type` is the closed ChangeTypes set; `change_reason`
// is operator free text, so it gets grouped on, never interpreted.
//
// Two properties of the source worth knowing here:
//   - The lines arrive UNSORTED. The lambda sorts with a unary function where Array.sort wants a
//     comparator, which leaves the array untouched — so we sort rather than trusting the order.
//   - The reason is usually one value repeated over every line (61 × "cant get core dpth" is real
//     production data), which is why grouping collapses these logs so hard.

/** One parsed line of the log. */
export interface SampleChange {
	sampleId: string;
	changeType: ChangeType;
	reason: string;
}

/** Every sample that got the same change for the same reason, collapsed into one entry. */
export interface SampleChangeGroup {
	changeType: ChangeType;
	/** Operator free text. Empty when the log carried no reason. */
	reason: string;
	/** Sample ids in display order — numeric ones ascending, then any non-numeric. */
	sampleIds: string[];
	count: number;
	/** `sampleIds` with consecutive runs collapsed, e.g. "1–12, 20, C7". */
	range: string;
}

export interface SampleChangesSummary {
	/** Largest group first. */
	groups: SampleChangeGroup[];
	/** Lines that did not match the log's shape, verbatim. Ops data — a format change should
	 *  degrade to showing the raw line, never to dropping it. */
	unparsed: string[];
	/** Changed samples across every group. */
	total: number;
}

const LINE_PREFIX = /^-\s*Sample\s+/;
const NUMERIC = /^\d+$/;
/** A run this long or longer is worth collapsing; "1, 2" reads better than "1–2". */
const MIN_RUN = 3;
// The ChangeTypes set from @rogoag/shared-ts, in its order, re-declared rather than imported as
// a value: this module is reachable from the @domain/CRM/job barrel, so a runtime dependency on
// shared-ts would break every test that mocks that package. `satisfies Record<ChangeType, …>`
// keeps it honest — a change type added upstream fails to compile here until it is listed, and a
// key shared-ts does not have is rejected. Same guard CHANGE_TYPE_LABELS uses in OpsMap.
const CHANGE_TYPE_ORDER = new Map<string, number>(
	Object.entries({
		None: 0,
		Add: 1,
		Move: 2,
		Delete: 3,
		Skip: 4,
		"Select Backup Point": 5,
	} satisfies Record<ChangeType, number>),
);

const isChangeType = (value: string): value is ChangeType => CHANGE_TYPE_ORDER.has(value);

/** A missing `change_reason` reaches Airtable as the template literal's stringified value. */
const readReason = (raw: string): string => (raw === "undefined" || raw === "null" ? "" : raw);

function parseLine(line: string): SampleChange | null {
	const trimmed = line.trim();
	if (!LINE_PREFIX.test(trimmed)) return null;
	const [id = "", type = "", ...reasonParts] = trimmed.replace(LINE_PREFIX, "").split(",");
	const sampleId = id.trim();
	const changeType = type.trim();
	// The reason is the rest of the line, so one containing a comma survives intact.
	if (!sampleId || !isChangeType(changeType)) return null;
	return { sampleId, changeType, reason: readReason(reasonParts.join(",").trim()) };
}

/** Parse the log into one entry per changed sample, keeping unrecognized lines aside. */
export function parseSampleChanges(raw: string | undefined): {
	changes: SampleChange[];
	unparsed: string[];
} {
	const changes: SampleChange[] = [];
	const unparsed: string[] = [];
	for (const line of (raw ?? "").split("\n")) {
		if (!line.trim()) continue;
		const change = parseLine(line);
		if (change) changes.push(change);
		else unparsed.push(line.trim());
	}
	return { changes, unparsed };
}

/** Orders two sample ids the way every "numeric ids ascending, then non-numeric" surface in the
 *  Skips flow wants them: numeric ids by value, with any non-numeric one ("C14") sorted after
 *  all of them. Two ids of the same kind compare equal — relies on `Array.prototype.sort`'s
 *  stability (guaranteed since ES2019) to leave their relative order exactly as it arrived. */
export function compareSampleIds(a: string, b: string): number {
	const aNumeric = NUMERIC.test(a);
	const bNumeric = NUMERIC.test(b);
	if (aNumeric && bNumeric) return Number(a) - Number(b);
	if (aNumeric !== bNumeric) return aNumeric ? -1 : 1;
	return 0;
}

/** Order sample ids for display: numeric ascending first, then non-numeric ("C14") in source
 *  order. Both are deduped. */
function orderSampleIds(sampleIds: string[]): string[] {
	return [...new Set(sampleIds)].sort(compareSampleIds);
}

/** Collapse consecutive numeric ids into ranges: [1,2,3,7,9,10] → "1–3, 7, 9, 10". Non-numeric
 *  ids are listed as-is. Expects ids already through `orderSampleIds`. */
export function formatSampleIdRanges(sampleIds: string[]): string {
	const parts: string[] = [];
	let run: number[] = [];

	const flush = () => {
		if (run.length === 0) return;
		if (run.length >= MIN_RUN) parts.push(`${run[0]}–${run[run.length - 1]}`);
		else parts.push(...run.map(String));
		run = [];
	};

	for (const id of sampleIds) {
		if (!NUMERIC.test(id)) {
			flush();
			parts.push(id);
			continue;
		}
		const n = Number(id);
		if (run.length > 0 && n !== run[run.length - 1] + 1) flush();
		run.push(n);
	}
	flush();
	return parts.join(", ");
}

/** Group the log by change type and reason — the shape worth showing, since the same reason
 *  typically repeats over every line. Largest group first, ties broken by ChangeTypes order so
 *  the result is stable. */
export function summarizeSampleChanges(raw: string | undefined): SampleChangesSummary {
	const { changes, unparsed } = parseSampleChanges(raw);

	const byKey = new Map<string, SampleChange[]>();
	for (const change of changes) {
		// NUL separates the halves: reasons are free text and one change type contains
		// spaces ("Select Backup Point"), so a printable separator could be ambiguous.
		const key = `${change.changeType}\u0000${change.reason}`;
		const existing = byKey.get(key);
		if (existing) existing.push(change);
		else byKey.set(key, [change]);
	}

	const groups = [...byKey.values()]
		.map((entries) => {
			const sampleIds = orderSampleIds(entries.map((e) => e.sampleId));
			return {
				changeType: entries[0].changeType,
				reason: entries[0].reason,
				sampleIds,
				count: sampleIds.length,
				range: formatSampleIdRanges(sampleIds),
			};
		})
		.sort(
			(a, b) =>
				b.count - a.count ||
				(CHANGE_TYPE_ORDER.get(a.changeType) ?? 0) - (CHANGE_TYPE_ORDER.get(b.changeType) ?? 0) ||
				a.reason.localeCompare(b.reason),
		);

	return { groups, unparsed, total: groups.reduce((sum, g) => sum + g.count, 0) };
}
